import { useEffect, useMemo, useState } from 'react';

// Keys for localStorage to throttle prompts
const LS_DISMISSED_AT = 'pwa-install-dismissed-at';
const LS_INSTALLED = 'pwa-installed-flag';

function isInStandaloneMode() {
  if (typeof window === 'undefined') return false;
  // iOS Safari
  const iosStandalone = (window.navigator as any).standalone === true;
  // All modern browsers
  const displayMode = window.matchMedia('(display-mode: standalone)').matches;
  return iosStandalone || displayMode;
}

function shouldShowAgain(intervalDays = 7) {
  try {
    const last = localStorage.getItem(LS_DISMISSED_AT);
    if (!last) return true;
    const diff = Date.now() - parseInt(last, 10);
    return diff > intervalDays * 24 * 60 * 60 * 1000;
  } catch {
    return true;
  }
}

export function usePwaInstallPromptModal() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);

  // Compute platform hints
  const platform = useMemo(() => {
    if (typeof navigator === 'undefined') return { isIOS: false, isAndroid: false };
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in (document as any));
    const isAndroid = /Android/.test(ua);
    return { isIOS, isAndroid };
  }, []);

  useEffect(() => {
    // If already installed, never show
    if (isInStandaloneMode()) {
      try { localStorage.setItem(LS_INSTALLED, '1'); } catch {}
      return;
    }

    const onAppInstalled = () => {
      try { localStorage.setItem(LS_INSTALLED, '1'); } catch {}
      setShowModal(false);
    };
    window.addEventListener('appinstalled', onAppInstalled);

    // Listen for native event (Chrome/Android typically)
    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowModal(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Fallback: if native event never fires (e.g., iOS or some Android browsers),
    // show our guidance modal occasionally.
    const timeout = window.setTimeout(() => {
      const installed = isInStandaloneMode();
      if (installed) return;
      const allow = shouldShowAgain(0); // show every time until installed
      if (allow) {
        setShowModal(true);
      }
    }, 4000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onAppInstalled);
      window.clearTimeout(timeout);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt?.prompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice.catch(() => ({ outcome: 'dismissed' }));
      if (outcome !== 'accepted') {
        try { localStorage.setItem(LS_DISMISSED_AT, String(Date.now())); } catch {}
      }
      setShowModal(false);
    } else {
      // No native prompt available; just persist dismissal throttle
      try { localStorage.setItem(LS_DISMISSED_AT, String(Date.now())); } catch {}
      setShowModal(false);
    }
  };

  const dismiss = (hours = 12) => {
    try { localStorage.setItem(LS_DISMISSED_AT, String(Date.now() - 12 * 60 * 60 * 1000 + hours * 60 * 60 * 1000)); } catch {}
    setShowModal(false);
  };

  return { showModal, setShowModal, handleInstall, deferredPrompt, dismiss, ...platform };
}
